import { defineStore } from "pinia";
import ChatMeRoomRepository from "@/repositories/Api/ChatMeRoomRepository.js";
import IntegrationRepository from "@/repositories/Api/IntegrationRepository.js";
import { connectSocketChatMeLib, disconnectManager } from "@/libs/socket";

const useChatPartnerStore = defineStore("chatPartner", {
  state: () => ({
    isConnectedChatMeRoom: false,
    isConnectedChatMeMessage: false,
    isFetchingTokenAndDomain: false,
    retryRoomIntervalId: null,
    retryMessageIntervalId: null,
    chatMeDomain: null,
    chatMeToken: null,
    roomSocket: null,
    messageSocket: null,
    profileId: null,
    userTyping: null,
    isConnectedToInternet: navigator?.onLine,
    countRoom: {
      request: 0,
      joinByMe: 0,
    },
    totalCountRoom: 0,
    counter: 0,
    tryAttemptConnectSocketRoom: 5,
    tryAttemptConnectSocketMessage: 5,
  }),
  getters: {},
  actions: {
    setTotalCountRoom(totalCountRoom) {
      this.totalCountRoom = totalCountRoom;
    },
    updateInternetStatus() {
      this.isConnectedToInternet = navigator?.onLine;
    },
    async getTokenAndDomain() {
      const merchant = await this.getMerchantChatMeInfo();
      if (
        merchant?.isEnablePrivateChat &&
        merchant?.domain &&
        merchant?.appId &&
        merchant?.appSecret &&
        merchant?.appSecret
      ) {
        ChatMeRoomRepository.getTokenAndDomain().then((response) => {
          const data = response?.data?.data || null;
          if (data) {
            this.chatMeDomain = data?.domain || null;
            this.chatMeToken = data?.token || null;
            if (this.chatMeToken && this.chatMeDomain) {
              this.connectSockets();
            }
          }
        });
      }
    },
    connectSockets() {
      if (!this.isConnectedChatMeRoom) {
        this.connectSocketChatMeRoom(this.chatMeDomain, this.chatMeToken);
      }
      if (!this.isConnectedChatMeMessage) {
        this.connectSocketChatMeMessage(this.chatMeDomain, this.chatMeToken);
      }
    },
    connectSocketChatMeRoom(domain, token) {
      const namespace = "/room";
      this.roomSocket = connectSocketChatMeLib(domain, namespace, token);
      this.setupRoomSocketEvents();
    },
    connectSocketChatMeMessage(domain, token) {
      const namespace = "/message";
      this.messageSocket = connectSocketChatMeLib(domain, namespace, token);
      this.setupMessageSocketEvents();
    },
    setupRoomSocketEvents() {
      this.roomSocket.on("connect", this.onRoomConnect);
      this.roomSocket.on("disconnect", this.onRoomDisconnect);
      this.roomSocket.on("connect_error", this.onRoomConnectError);
    },
    setupMessageSocketEvents() {
      this.messageSocket.on("connect", this.onMessageConnect);
      this.messageSocket.on("disconnect", this.onMessageDisconnect);
      this.messageSocket.on("connect_error", this.onMessageConnectError);
      this.eventListenMessage();
    },
    onRoomConnect() {
      this.isConnectedChatMeRoom = true;
      this.tryAttemptConnectSocketRoom = 5;
      console.log("Connected to roomSocket");
    },
    onRoomDisconnect() {
      this.isConnectedChatMeRoom = false;
      console.log("Disconnected from roomSocket");
    },
    async onRoomConnectError(error) {
      this.isConnectedChatMeRoom = false;
      if (
        error?.message === "unauthorized" &&
        this.tryAttemptConnectSocketRoom >= 1
      ) {
        this.tryAttemptConnectSocketRoom--;
        this.roomSocket.auth.token = await this.refreshToken();
        console.log(this.roomSocket.auth.token); //delete
        this.roomSocket.connect();
      }
    },
    onMessageConnect() {
      this.isConnectedChatMeMessage = true;
      this.tryAttemptConnectSocketMessage = 5;
      this.checkRequestMessage();
      this.getCountMessage();
      console.log("Connected to messageSocket");
    },
    onMessageDisconnect() {
      this.isConnectedChatMeMessage = false;
      console.log("Disconnected from messageSocket");
    },
    async onMessageConnectError(error) {
      this.isConnectedChatMeMessage = false;
      if (
        error?.message === "unauthorized" &&
        this.tryAttemptConnectSocketMessage >= 1
      ) {
        this.tryAttemptConnectSocketMessage--;
        this.messageSocket.auth.token = await this.refreshToken();
        this.messageSocket.connect();
      }
    },
    async refreshToken() {
      try {
        const response = await ChatMeRoomRepository.getTokenAndDomain();
        const newToken = response?.data?.data?.token || null;
        this.chatMeToken = newToken;
        return newToken;
      } catch (error) {
        console.error("Error refreshing token:", error);
        return null;
      }
    },
    getCountMessage() {
      try {
        this.messageSocket.emit("message:totalBadge", {}, (ackData) => {
          this.countRoom.joinByMe = ackData?.data?.total || 0;
        });
      } catch (error) {
        console.error("Error getting message count:", error);
      }
    },
    checkRequestMessage() {
      ChatMeRoomRepository.getRequestJoin().then((response) => {
        const roomIds = response?.data?.data || [];
        const merchantRoomIds = roomIds.map(({ chatMeRoomId }) => chatMeRoomId);
        if (merchantRoomIds.length >= 1) {
          this.countRoom.request = merchantRoomIds.length;
        } else {
          this.countRoom.request = 0;
        }
      });
    },
    eventListenMessage(receiveMessage = null) {
      if (!this.messageSocket) return;
      this.offReceiveMessage();
      this.messageSocket.on("message:receiveMessage", (data) => {
        if (typeof receiveMessage == "function") {
          return receiveMessage(data);
        }
        this.checkRequestMessage();
        this.getCountMessage();
      });
    },
    offReceiveMessage() {
      if (this.messageSocket) {
        this.messageSocket.off("message:receiveMessage");
      }
    },
    disconnectRoomAndMessageSocket() {
      if (this.messageSocket) {
        this.offEvents();
        this.messageSocket.disconnect();
      }
      if (this.roomSocket) this.roomSocket.disconnect();
      disconnectManager();
      this.$reset();
    },
    offEvents() {
      if (this.messageSocket) {
        this.messageSocket.off("message:receiveMessage");
        this.messageSocket.off("message:onTyping");
      }
    },
    async getMerchantChatMeInfo() {
      const merchantInfo = await IntegrationRepository.showChatMe();
      return merchantInfo?.data?.data || null;
    },
  },
});

export default useChatPartnerStore;
